import { Component, DoCheck, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { GridColumnDef, GridDefinition, FilterGroup, FilterRule, LookupOption } from './grid-types';
import { GridApiService } from './services/grid-api.service';

// How long typing must pause before a checkOnChange field is sent to the server. Leaving the field checks at once.
const FIELD_CHECK_DELAY_MS = 3000;

@Component({
  selector: 'wc-record-editor',
  standalone: false,
  styleUrls: ['./grid-theme.css'],
  template: `
  <div class="wcg-modal">
  <div class="wcg-modal__panel">
    <div class="wcg-modal__header">
      <strong class="wcg-modal__title">{{title}}</strong>
      <span class="wcg-modal__spacer"></span>
      <button class="wcg-btn wcg-btn--ghost" (click)="cancel.emit()">Close</button>
    </div>

    <div class="wcg-form">
      <ng-container *ngFor="let c of editableColumns">
        <div class="wcg-form__label">
          <label class="wcg-form__label-text">
            {{c.header}}
            <span *ngIf="c.required" class="wcg-form__req">*</span>
          </label>

          <div class="wcg__muted" *ngIf="c.readOnlyReason">{{c.readOnlyReason}}</div>
        </div>

        <div class="wcg-form__field"
             [class.wcg-form__field--readonly]="!!c.readOnlyReason || !isEditable(c) || isLocked(c)">
          <ng-container [ngSwitch]="editorType(c)">
            <wc-lookup-editor *ngSwitchCase="'lookup'"
                              class="wcg__control"
                              [gridKey]="def.gridKey"
                              [lookupKey]="c.lookupKey!"
                              [filter]="getCascadeFilter(c)"
                              [lookups]="lookups"
                              [(value)]="model[c.field]"
                              [required]="c.required"
                              [disabled]="isLocked(c)">
            </wc-lookup-editor>

            <input *ngSwitchCase="'text'" class="wcg__input" [(ngModel)]="model[c.field]" [disabled]="!isEditable(c) || isLocked(c)"
                   (ngModelChange)="onFieldInput(c, $event)" (blur)="onFieldBlur(c)" />
            <input *ngSwitchCase="'number'" class="wcg__input wcg__input--number" type="number" [(ngModel)]="model[c.field]" [disabled]="!isEditable(c)" />
            <input *ngSwitchCase="'date'" class="wcg__input wcg__input--date" type="date" [ngModel]="toIsoDateOnly(model[c.field])" [disabled]="!isEditable(c)"
       (ngModelChange)="model[c.field] = fromIsoDateOnly($event)" />
            <label *ngSwitchCase="'bool'" class="wcg__checkbox">
              <input type="checkbox" [(ngModel)]="model[c.field]" [disabled]="!isEditable(c)" />
              <span class="wcg__checkbox-ui"></span>
            </label>
            <input *ngSwitchCase="'datetime'"
               class="wcg__input wcg__input--date"
               type="datetime-local"
               [ngModel]="toLocalDateTime(model[c.field])"
               (ngModelChange)="model[c.field] = fromLocalDateTime($event)" [disabled]="!isEditable(c)" />
          </ng-container>

          <div *ngIf="errors[c.field]" class="wcg-form__error">
            {{errors[c.field]}}
          </div>
          <div *ngIf="checking[c.field]" class="wcg__muted">Checking…</div>
          <div *ngFor="let n of fieldNotes[c.field]"
               [class.wcg-form__note]="!blockedBy[c.field]"
               [class.wcg-form__error]="!!blockedBy[c.field]">{{n}}</div>
        </div>
      </ng-container>
    </div>

    <div class="wcg-modal__footer">
      <button class="wcg-btn wcg-btn--success" (click)="onSave()" [disabled]="saveBlocked">Save</button>
      <button class="wcg-btn wcg-btn--ghost" (click)="cancel.emit()">Cancel</button>
    </div>
  </div>
</div>

  `
})
export class WcRecordEditorComponent implements DoCheck, OnDestroy {
  @Input() def!: GridDefinition;
  @Input() title = 'Edit record';
  @Input() filter?: FilterGroup|null; // unused; cascading lookups read from the in-progress model below
  @Input() model: Record<string, any> = {};
  @Input() mode: 'edit' | 'create' = 'edit';
  /** The grid's context, sent with field checks the same way it is sent with saves. */
  @Input() context: Record<string, any> = {};

  @Output() save = new EventEmitter<Record<string, any>>();
  @Output() cancel = new EventEmitter<void>();

  @Input() lookups?: Record<string, LookupOption[]>;

  errors: Record<string, string> = {};

  /** Server messages about checkOnChange fields (e.g. "this email already has an account"), by field. */
  fieldNotes: Record<string, string[]> = {};
  checking: Record<string, boolean> = {};
  private checkTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  private checkSubs: Record<string, Subscription> = {};
  private checkedSig: Record<string, string> = {};
  /** Checked fields whose value means the record can't be added, and the fields each one locks (see FieldCheckResult.blocked). */
  blockedBy: Record<string, string[]> = {};

  get saveBlocked(): boolean {
    return Object.keys(this.blockedBy).length > 0;
  }

  isLocked(c: GridColumnDef): boolean {
    return Object.values(this.blockedBy).some(locked => locked.includes(c.field));
  }

  constructor(private api: GridApiService) {}

  ngOnDestroy(): void {
    Object.values(this.checkTimers).forEach(t => clearTimeout(t));
    Object.values(this.checkSubs).forEach(s => s.unsubscribe());
  }

  private shouldCheck(c: GridColumnDef): boolean {
    return this.mode === 'create' && !!c.checkOnChange;
  }

  onFieldInput(c: GridColumnDef, value: any) {
    if (!this.shouldCheck(c)) return;
    // What was said about the previous value no longer applies to this one.
    this.cancelCheck(c.field);
    this.fieldNotes[c.field] = [];
    this.checkedSig[c.field] = '';
    delete this.blockedBy[c.field];
    if (value === null || value === undefined || String(value).trim() === '') return;
    this.checkTimers[c.field] = setTimeout(() => this.runCheck(c), FIELD_CHECK_DELAY_MS);
  }

  onFieldBlur(c: GridColumnDef) {
    if (!this.shouldCheck(c)) return;
    clearTimeout(this.checkTimers[c.field]);
    this.runCheck(c);
  }

  private cancelCheck(field: string) {
    clearTimeout(this.checkTimers[field]);
    this.checkSubs[field]?.unsubscribe();
    this.checking[field] = false;
  }

  private runCheck(c: GridColumnDef) {
    const value = this.model[c.field];
    if (value === null || value === undefined || String(value).trim() === '') return;

    const sig = this.checkSig(c);
    if (this.checkedSig[c.field] === sig) return;
    this.checkedSig[c.field] = sig;

    this.cancelCheck(c.field);
    this.checking[c.field] = true;
    this.checkSubs[c.field] = this.api
      .fieldCheck(this.def.gridKey, { field: c.field, record: { ...this.model }, context: this.context })
      .subscribe(r => {
        this.checking[c.field] = false;
        this.fieldNotes[c.field] = r.messages;
        if (r.blocked) this.blockedBy[c.field] = r.lockedFields ?? [];
        else delete this.blockedBy[c.field];
        this.applyServerValues(r.values);
        // The server may have changed a lookup it is keyed on (e.g. moved Company to where the account lives); that
        // answer already accounts for it, so leaving the field again must not re-check and replace the note.
        this.checkedSig[c.field] = this.checkSig(c);
      });
  }

  // Lookup fields (e.g. Company) are part of the signature: the same email can mean something different elsewhere.
  private checkSig(c: GridColumnDef): string {
    return JSON.stringify([String(this.model[c.field] ?? '').trim().toLowerCase(),
      ...this.def.columns.filter(o => o.lookupKey).map(o => this.model[o.field] ?? null)]);
  }

  // Values the server says take precedence (e.g. an existing Enverus account's name and email) replace what was typed.
  // Set directly on the model, so they don't count as typing and don't re-trigger a check.
  private applyServerValues(values?: Record<string, any> | null) {
    if (!values) return;
    for (const [field, v] of Object.entries(values)) {
      if (v === null || v === undefined || !(field in this.model)) continue;
      this.model[field] = v;
      delete this.errors[field];
    }
  }

  /** Per-cascading-column FilterGroup derived from the in-progress model. */
  private cascadeFilters: Record<string, FilterGroup | null> = {};
  private cascadeSig: Record<string, string> = {};

  ngDoCheck(): void {
    if (!this.def?.columns) return;
    for (const c of this.def.columns) {
      if (!c.cascadeLookup) continue;
      const rules: FilterRule[] = [];
      // Only other lookup-driven columns (e.g. a parent dropdown like Unit Set) can plausibly
      // drive this cascade — plain text/number/bool fields (Display Name, Field Format, ...)
      // never should. Scanning every model key here meant typing into an unrelated text field
      // still changed the computed filter's identity on every keystroke, which re-triggered
      // the lookup HTTP call each time and made the whole popup feel like it was refreshing.
      for (const other of this.def.columns) {
        if (other.field === c.field || !other.lookupKey) continue;
        const v = this.model[other.field];
        if (v === null || v === undefined || v === '') continue;
        rules.push({ field: other.field, op: 'Eq', value: v });
      }
      const sig = JSON.stringify(rules);
      if (this.cascadeSig[c.field] !== sig) {
        this.cascadeSig[c.field] = sig;
        this.cascadeFilters[c.field] = { logic: 'AND', rules };
      }
    }
  }

  getCascadeFilter(c: GridColumnDef): FilterGroup | null {
    if (!c.cascadeLookup) return null;
    return this.cascadeFilters[c.field] ?? null;
  }

  get editableColumns(): GridColumnDef[] {
    // In create mode include creatable fields: heuristic = editable or required or idField.
    const cols = this.def.columns
      .filter(c => !c.field.startsWith('__') && (!c.hideOnAdd || this.mode != 'create') && (!c.hideOnEdit || this.mode != 'edit'));

    // include id field first for create
    if (this.mode === 'create') {
      const id = cols.find(c => c.field === this.def.idField);
      const rest = cols.filter(c => c.field !== this.def.idField);
      return id ? [id, ...rest] : cols;
    }
    return cols;
  }

  isEditable(c: GridColumnDef) {
    return this.mode === 'create' ? (c.editable || c.required || c.field === this.def.idField) : c.editable;
  }

  editorType(c: GridColumnDef): 'lookup'|'text'|'number'|'date'|'bool'|'datetime' {
    if (c.lookupKey) return 'lookup';
    switch (c.dataType) {
      case 'Int':
      case 'Double': return 'number';
      case 'Date': return 'date';
      case 'DateTime': return 'datetime';
      case 'Bool': return 'bool';
      default: return 'text';
    }
  }

  onSave() {
    if (this.saveBlocked) return;
    this.errors = {};
    for (const c of this.editableColumns) {
      if (!c.required) continue;
      const v = this.model[c.field];
      if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
        this.errors[c.field] = 'Required';
      }
    }
    if (Object.keys(this.errors).length > 0) return;
    this.save.emit(this.model);
  }

  // ----- DATE (naive) -----
  toIsoDateOnly(val: any): string | null {
    if (!val) return null;
    const s = String(val);
    // Accept "2026-02-13", or "2026-02-13T12:34:56"
    return s.length >= 10 ? s.substring(0, 10) : null;
  }

  fromIsoDateOnly(val: string | null): string | null {
    // Keep as naive date-only string
    return val && val.trim() ? val : null;
  }

  // ----- DATETIME (naive, no timezone) -----
  toLocalDateTime(val: any): string | null {
    if (!val) return null;
    const s = String(val).trim();

    // If server sends "YYYY-MM-DD HH:mm:ss", normalize:
    const normalized = s.includes(' ') ? s.replace(' ', 'T') : s;

    // Input datetime-local wants "YYYY-MM-DDTHH:mm" (seconds optional)
    if (normalized.length >= 16) return normalized.substring(0, 16); // drop seconds for the control
    return normalized;
  }

  fromLocalDateTime(val: string | null): string | null {
    if (!val || !val.trim()) return null;

    // datetime-local gives "YYYY-MM-DDTHH:mm" (no tz). Add seconds so server gets stable format:
    return val.length === 16 ? `${val}:00` : val;
  }
}
